/** Pure selectors over AppState. Owned by the main loop. */
import type { Category, Place } from "@/contracts/data";
import { displayTravel } from "@/contracts/data";
import type { AppState } from "@/contracts/store";

const norm = (s: string) => s.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();

/** Places listed in the panel, in display order (group → category → query → sort). */
export function selectVisiblePlaces(s: AppState): Place[] {
  const d = s.data;
  if (!d) return [];
  const q = norm(s.query.trim());
  let list = d.places.filter((p) => !p.isSite);
  if (q) {
    // search spans every group; the status word ("upcoming", "proposed") matches every place it tags
    const status = (p: Place) => (p.status === "existing" ? "" : p.status.replace(/-/g, " "));
    list = list.filter((p) => norm(`${p.name} ${p.locality ?? ""} ${p.notes ?? ""} ${status(p)}`).includes(q));
  } else {
    list = list.filter((p) => p.group === s.group && (!s.category || p.category === s.category));
  }
  const catOrder = new Map(d.categories.map((c) => [c.id, c.order]));
  const by = {
    time: (p: Place) => displayTravel(p)?.minutes ?? Number.POSITIVE_INFINITY,
    distance: (p: Place) => displayTravel(p)?.km ?? p.crowKm,
    name: (_p: Place) => 0,
  }[s.sort];
  return [...list].sort((a, b) => {
    if (!s.category && !q) {
      const c = (catOrder.get(a.category) ?? 0) - (catOrder.get(b.category) ?? 0);
      if (c) return c;
    }
    const d1 = by(a) - by(b);
    return d1 || a.name.localeCompare(b.name);
  });
}

export function selectCategoriesForGroup(s: AppState): (Category & { count: number })[] {
  const d = s.data;
  if (!d) return [];
  return d.categories
    .filter((c) => c.group === s.group)
    .sort((a, b) => a.order - b.order)
    .map((c) => ({ ...c, count: d.places.filter((p) => p.category === c.id && !p.isSite).length }));
}

export function selectPlace(s: AppState, id: string | null): Place | null {
  if (!id || !s.data) return null;
  return s.data.places.find((p) => p.id === id) ?? null;
}

export function selectCategory(s: AppState, id: string | null): Category | null {
  if (!id || !s.data) return null;
  return s.data.categories.find((c) => c.id === id) ?? null;
}
